// src/pages/web/HomePage.jsx
import React, { useState, useEffect } from "react";
import {
    Box,
    Container,
    Typography,
    Button,
    Grid,
    Paper,
    Card,
    CardContent,
    CardMedia,
    CardActionArea,
    Stack,
    TextField,
    CircularProgress,
    Divider,
    ImageList,
    ImageListItem,
    Chip,
} from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import WebLayout from "@/components/web/WebLayout";
import {
    ROUTES,
    SAMPLE_VIDEO_URL,
    PLACEHOLDER_IMAGES,
} from "@/utils/web/constants";
import Icon from "@/theme/vectors/bundle";
import Testimonials from "@/components/web/Testimonials";
import { useCart } from "@/hooks/web/useCart";
import axios from "axios";
import { calculateDiscountPercentage } from "@/utils/products/variantHelpers";

const PRODUCT_IDS = {
    PRODUCT_1: '6VCO8E7RQS65RWKP',
    PRODUCT_2: 'HHCUUXZQCFJQSUK9'
};

const HomePage = () => {
    const { t } = useTranslation();
    const { addToCart, loading: cartLoading } = useCart();
    const navigate = useNavigate();
    const location = useLocation();
    
    // Product state
    const [products, setProducts] = useState({});
    const [selectedProductId, setSelectedProductId] = useState(PRODUCT_IDS.PRODUCT_1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    
    // Computed current product
    const product = products[selectedProductId];

    // Scroll to top on initial load
    useEffect(() => {
        const handleScroll = () => {
            const hash = location.hash;
            
            // If there's a hashtag, try to scroll to that element
            if (hash) {
                const targetId = hash.substring(1); // Remove the # character
                const targetElement = document.getElementById(targetId);
                
                if (targetElement) {
                    // Found a valid element with the ID from hashtag
                    setTimeout(() => {
                        targetElement.scrollIntoView({ behavior: 'smooth' });
                    }, 300);
                } else {
                    // No element found with that ID, scroll to top
                    window.scrollTo(0, 0);
                }
            } else {
                // No hashtag, just scroll to top
                window.scrollTo(0, 0);
            }
        };
        
        // Execute scroll logic after the component is mounted
        handleScroll();
    }, [location]); // Re-run when location changes

    // Fetch both products in parallel
    useEffect(() => {
        const fetchProducts = async () => {
            setLoading(true);
            try {
                const [response1, response2] = await Promise.all([
                    axios.get(`https://api.emboxvn.com/product/${PRODUCT_IDS.PRODUCT_1}`),
                    axios.get(`https://api.emboxvn.com/product/${PRODUCT_IDS.PRODUCT_2}`)
                ]);
                
                if (response1.data.isSuccess && response2.data.isSuccess) {
                    setProducts({
                        [PRODUCT_IDS.PRODUCT_1]: response1.data.data,
                        [PRODUCT_IDS.PRODUCT_2]: response2.data.data
                    });
                } else {
                    setError('Could not fetch product data');
                }
            } catch (err) {
                setError('Failed to load products');
                console.error('Error fetching products:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, []);
    
    // Set default variant and image when product changes
    useEffect(() => {
        if (product && product.variants && product.variants.length > 0) {
            const secondVariant = product.variants[1] || product.variants[0];
            setSelectedVariant(secondVariant);
            setQuantity(1);
            
            // Set main image from the variant's media or product's media
            if (secondVariant.media && secondVariant.media.length > 0) {
                setSelectedImage(secondVariant.media[0].media_url);
            } else if (product.media && product.media.length > 0) {
                setSelectedImage(product.media[0].media_url);
            }
        }
    }, [selectedProductId, products]);

    // Handle quantity change
    const handleQuantityChange = (e) => {
        const value = parseInt(e.target.value);
        if (value > 0) {
            setQuantity(value);
        }
    };

    // Handle variant selection
    const handleVariantChange = (variant) => {
        setSelectedVariant(variant);
        
        // Update main image if variant has media
        if (variant.media && variant.media.length > 0) {
            setSelectedImage(variant.media[0].media_url);
        }
    };
    
    // Handle image selection
    const handleImageSelect = (imageUrl) => {
        setSelectedImage(imageUrl);
    };

    // Handle buy now with redirection to cart
    const handleBuyNow = () => {
        if (product && selectedVariant) {
            addToCart(
                {
                    id: product.id,
                    name: product.name,
                    media: product.media
                },
                {
                    id: selectedVariant.id,
                    price: selectedVariant.price,
                    attribute_summary: product.attributes && product.attributes.length > 0 ? 
                        product.attributes[0].values[
                            Math.min(
                                product.variants.findIndex(v => v.id === selectedVariant.id),
                                product.attributes[0].values.length - 1
                            )
                        ].value : ''
                },
                quantity
            );
            
            // Navigate to cart page after adding product
            navigate(ROUTES.CART);
        }
    };

    return (
        <WebLayout>
            {/* Hero section with image */}
            <Box
                sx={{
                    width: "100%",
                    position: "relative",
                    height: { xs: "50vh", md: "70vh" },
                    overflow: "hidden",
                    mb: 6,
                    bgcolor: "#525659",
                }}
            >
                <Box
                    component="img"
                    src="/assets/embox-main.png"
                    alt="Banner"
                    sx={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                    }}
                />

            </Box>

            {/* Main content */}
            <Container maxWidth="lg" sx={{ mb: 8 }}>
                {/* EMBOX Product Showcase */}
                <Box sx={{ mb: 8 }} id="product-showcase">
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography color="error">{error}</Typography>
                        </Box>
                    ) : Object.keys(products).length > 0 ? (
                        <>
                            {/* Product Selector Buttons */}
                            <Stack 
                                direction={{ xs: 'column', sm: 'row' }} 
                                spacing={2} 
                                sx={{ mb: 4, justifyContent: 'center' }}
                            >
                                {Object.entries(products).map(([id, prod]) => (
                                    <Button
                                        key={id}
                                        variant={selectedProductId === id ? "contained" : "outlined"}
                                        onClick={() => setSelectedProductId(id)}
                                        size="large"
                                        sx={{
                                            px: 4,
                                            py: 1.5,
                                            minWidth: { xs: '100%', sm: '200px' },
                                            fontWeight: 'bold',
                                            borderWidth: 2,
                                            '&:hover': {
                                                borderWidth: 2
                                            }
                                        }}
                                    >
                                        {prod.name}
                                    </Button>
                                ))}
                            </Stack>
                            
                            {/* Product Details */}
                            {product && (
                        <Grid container spacing={4}>
                            {/* Product Images - Single column on mobile, side-by-side on desktop */}
                            <Grid item xs={12} md={6}>
                                <Box sx={{ mb: 3 }}>
                                    {/* Main image */}
                                    {selectedImage && (
                                        <Box
                                            component="img"
                                            src={selectedImage}
                                            alt={product.name}
                                            sx={{
                                                width: '100%',
                                                borderRadius: 2,
                                                boxShadow: 2,
                                                mb: 2,
                                                maxHeight: { xs: '300px', md: '400px' },
                                                objectFit: 'cover',
                                            }}
                                        />
                                    )}
                                    
                                    {/* Image gallery - Show images from current variant or product */}
                                    {(selectedVariant?.media?.length > 0 || product.media?.length > 0) && (
                                        <ImageList cols={4} gap={8} sx={{ mb: 2 }}>
                                            {/* Show images from variant if available, otherwise from product */}
                                            {(selectedVariant?.media?.length > 0 ? selectedVariant.media : product.media).map((media) => (
                                                <ImageListItem 
                                                    key={media.id}
                                                    onClick={() => handleImageSelect(media.media_url)}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        border: selectedImage === media.media_url ? '2px solid #FFDA61' : 'none',
                                                        borderRadius: '8px',
                                                        overflow: 'hidden'
                                                    }}
                                                >
                                                    <img
                                                        src={media.media_url}
                                                        alt={`${product.name} - ${media.id}`}
                                                        loading="lazy"
                                                        style={{ 
                                                            height: '80px',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                </ImageListItem>
                                            ))}
                                        </ImageList>
                                    )}
                                </Box>
                            </Grid>
                            
                            {/* Product Information */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom>
                                    {product.name}
                                </Typography>
                                
                                {/* Product variants */}
                                {product.attributes && product.attributes.length > 0 && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                            {product.attributes[0].name}:
                                        </Typography>
                                        
                                        {/* Switch from Stack to Grid for vertical layout */}
                                        <Grid container spacing={2} sx={{ mb: 2 }}>
                                            {product.attributes[0].values.map((value, index) => {
                                                // Find matching variant (if there's a direct match between attribute value index and variant)
                                                // Limit to available variants
                                                const variantIndex = Math.min(index, product.variants.length - 1);
                                                const variant = product.variants[variantIndex];
                                                const isSelected = selectedVariant && selectedVariant.id === variant.id;
                                                
                                                // Calculate discount percentage if applicable
                                                const discountPercentage = variant.prediscount_price && variant.prediscount_price > variant.price 
                                                    ? calculateDiscountPercentage(variant.prediscount_price, variant.price)
                                                    : 0;
                                                
                                                return (
                                                    <Grid item xs={12} key={value.id}>
                                                        <Box 
                                                            onClick={() => handleVariantChange(variant)}
                                                            sx={{ 
                                                                border: '1px solid',
                                                                borderColor: isSelected ? 'primary.main' : 'divider',
                                                                borderRadius: 1,
                                                                p: 2,
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s',
                                                                position: 'relative',
                                                                backgroundColor: isSelected ? 'rgba(255, 218, 97, 0.08)' : 'transparent',
                                                                boxShadow: isSelected ? '0 0 0 2px #FFDA61' : 'none',
                                                                '&:hover': {
                                                                    borderColor: 'primary.main',
                                                                    backgroundColor: 'rgba(255, 218, 97, 0.04)'
                                                                },
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center'
                                                            }}
                                                        >
                                                            {/* Left side - Variant name and option */}
                                                            <Box>
                                                                <Typography variant="subtitle1" fontWeight={isSelected ? 'bold' : 'normal'}>
                                                                    {value.value}
                                                                </Typography>
                                                                
                                                                {/* Price display */}
                                                                <Box sx={{ mt: 1 }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                                                                        <Typography 
                                                                            variant="body1"
                                                                            fontWeight="bold"
                                                                            color={isSelected ? "primary.main" : "text.primary"}
                                                                        >
                                                                            {new Intl.NumberFormat('vi-VN', {
                                                                                style: 'currency',
                                                                                currency: 'VND'
                                                                            }).format(variant.price)}
                                                                        </Typography>
                                                                        
                                                                        {discountPercentage > 0 && (
                                                                            <Typography 
                                                                                variant="body2" 
                                                                                color="text.secondary" 
                                                                                sx={{ 
                                                                                    textDecoration: 'line-through',
                                                                                    ml: 1
                                                                                }}
                                                                            >
                                                                                {new Intl.NumberFormat('vi-VN', {
                                                                                    style: 'currency',
                                                                                    currency: 'VND'
                                                                                }).format(variant.prediscount_price)}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                            
                                                            {/* Right side - Discount badge */}
                                                            {discountPercentage > 0 && (
                                                                <Chip 
                                                                    label={`-${discountPercentage}%`}
                                                                    color="error"
                                                                    size="small"
                                                                    sx={{ 
                                                                        fontWeight: 'bold',
                                                                        fontSize: '0.75rem',
                                                                        height: '24px',
                                                                        backgroundColor: '#ff4d4f',
                                                                        color: 'white'
                                                                    }}
                                                                />
                                                            )}
                                                            
                                                            {/* Selected indicator */}
                                                            {isSelected && (
                                                                <Box 
                                                                    sx={{ 
                                                                        position: 'absolute', 
                                                                        top: 0, 
                                                                        right: 0,
                                                                        p: 0.5,
                                                                        bgcolor: 'primary.main',
                                                                        color: 'white',
                                                                        borderBottomLeftRadius: 4
                                                                    }}
                                                                >
                                                                    <Icon name="check" size={16} />
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    </Box>
                                )}
                                
                                {/* Quantity selection */}
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mr: 2 }}>
                                        Số lượng:
                                    </Typography>
                                    <TextField
                                        type="number"
                                        inputProps={{ min: 1, max: 99 }}
                                        value={quantity}
                                        onChange={handleQuantityChange}
                                        variant="outlined"
                                        size="small"
                                        sx={{ width: '80px' }}
                                    />
                                </Box>
                                
                                {/* Buy Now button */}
                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={<Icon name="shoppingCart" />}
                                    onClick={handleBuyNow}
                                    disabled={cartLoading || !selectedVariant}
                                    sx={{ mb: 3, px: 4, py: 1.2 }}
                                    fullWidth
                                >
                                    {cartLoading ? t("common.processing") : t("cart.buyNow")}
                                </Button>
                                
                                {/* Product introduction */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="body1" dangerouslySetInnerHTML={{ __html: product.intro }} />
                                </Box>
                            </Grid>
                            
                            {/* Product description - Full width */}
                            <Grid item xs={12} data-product-details id="product-details">
                                <Divider sx={{ my: 4 }} />
                                <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom>
                                    Chi tiết sản phẩm
                                </Typography>
                                <Box sx={{ mt: 2 }}>
                                    <Typography 
                                        variant="body1" 
                                        component="div"
                                        dangerouslySetInnerHTML={{ __html: product.description }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                            )}
                        </>
                    ) : null}
                </Box>
                
                {/* Customer Testimonials Section */}
                <Testimonials />
            </Container>
        </WebLayout>
    );
};

export default HomePage;
